import { type ComponentPropsWithRef } from 'react';
import { Icon, type IconName } from './icon';
import styles from './variant-chip.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

export type RecipeVariant = 'quick' | 'classic' | 'bougie';

type VariantChipProps = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
  /** Which tier this chip identifies. */
  variant: RecipeVariant;
  /**
   * Pre-translated visible label (e.g. `t('recipes.variants.quick')`). The
   * caller owns i18n — this component never calls a translator. In icon-only
   * mode (`showLabel={false}`) the label is not rendered but becomes the
   * chip's accessible name (`aria-label` + `title`).
   */
  label: string;
  /** false → icon-only compact pill; the label becomes the accessible name. */
  showLabel?: boolean;
  /**
   * Override the tier glyph. Used by the meal plan's time badge, which wears
   * the tier's tint but reads a total cook time ("55 min") behind a `timer`
   * glyph — the same pill in a different mode, not a second tinted chip.
   */
  icon?: IconName;
};

/**
 * Icon per tier. Quick keeps the live recipe card's `timer` (speed) glyph
 * rather than the design token's `zap`, so the shipped Quick pill is
 * visually unchanged. Classic/Bougie follow the design's VARIANT_META.
 *
 * Exported so the create-recipe variant *picker* wears the same glyphs as this
 * identity pill — one tier, one icon, defined here.
 */
export const VARIANT_ICON: Record<RecipeVariant, IconName> = {
  quick: 'timer',
  classic: 'fork-knife',
  bougie: 'sparkles',
};

const VARIANT_CLASS: Record<RecipeVariant, string> = {
  quick: styles.quick,
  classic: styles.classic,
  bougie: styles.bougie,
};

/**
 * VariantChip — the Quick / Classic / Bougie identity pill. ONE definition:
 * RecipeCard floats it over the media, MealPlanGlance uses the compact
 * icon-only mode. Position-agnostic — the consumer places it (absolute
 * positioning is the card's concern, not the chip's). Presentational and
 * Server-Component-safe: no hooks, no handlers.
 */
export function VariantChip({
  variant,
  label,
  showLabel = true,
  icon,
  className,
  ...rest
}: VariantChipProps) {
  // Icon-only mode has no visible text, so name the chip for assistive tech.
  // Spread after these so a caller-supplied aria-label/title still wins.
  const compactA11y = showLabel ? {} : { 'aria-label': label, title: label };

  return (
    <span
      className={cx(styles.chip, VARIANT_CLASS[variant], !showLabel && styles.compact, className)}
      {...compactA11y}
      {...rest}
    >
      <Icon name={icon ?? VARIANT_ICON[variant]} size={13} strokeWidth={1.75} aria-hidden="true" />
      {showLabel ? label : null}
    </span>
  );
}
